import { createElement, type HTMLAttributes, type ReactNode } from "react";

/**
 * Session-replay text masking: the ONE way to say "this is somebody's words".
 *
 * posthog-js records the control surfaces (see useRouteAnalytics' NO_RECORDING for the
 * routes that never record). rrweb masks the text of any node whose element — or any
 * ancestor of it — matches `maskTextSelector`, which analytics.ts sets to MASK_SELECTOR.
 * Masking keeps the length (every non-space character becomes `*`), so a replay still
 * shows the layout, the buttons and the counts, just not what a person wrote.
 *
 * What counts as user text (2026-10-21 decision): usernames, collection / view / room
 * titles, captions and comments. Chrome — buttons, headings, counts, curated names
 * (Editor's Choice, quick setups, walls) — stays readable, which is the whole reason this
 * is a marker and not `maskTextSelector: "*"`.
 *
 * Attributes are NOT covered by the selector: analytics.ts' maskAttributeFn masks
 * `title`, `aria-label`, `href`, `download`, `alt` and friends on anything that carries or
 * sits inside a marker, and rewrites `/u/<name>`, `/feed/<id>` and `/room/<id>` in
 * href-like attributes everywhere.
 *
 * Usage:
 *   <span {...MASK}>{user.username}</span>
 *   <Masked>{collection.name}</Masked>
 *   <span {...maskIf(!c.editorsChoice)}>{c.name}</span>
 *
 * tools/checks/replay-mask.mts renders the marked components with a sentinel and fails
 * if a user string lands outside a marker, and statically guards new unmarked sites.
 */

export const MASK_ATTRIBUTE = "data-ph-mask";
export const MASK_SELECTOR = "[data-ph-mask]";

/** Spread onto any DOM element (or a component that forwards props to one). */
export const MASK = { "data-ph-mask": "" } as const;

/** MASK when `on`, nothing otherwise — for lists that mix user-made and curated names. */
export function maskIf(on: boolean): typeof MASK | Record<string, never> {
  return on ? MASK : {};
}

type MaskedProps = {
  /** The element to render; a `span` unless the content needs a block. */
  as?: "span" | "div" | "p";
  children?: ReactNode;
} & HTMLAttributes<HTMLElement>;

/** A masked wrapper element: `<Masked>{name}</Masked>`. */
export function Masked({ as = "span", children, ...rest }: MaskedProps) {
  return createElement(as, { ...rest, ...MASK }, children);
}
